/**
 * Paso Confirmar del asistente: la tarjeta «Vas a mandar» (29/10/2026), arriba de todo. Se lee de una pasada antes de
 * enviar: la previa REAL del mensaje (con un contacto de la audiencia, la misma de Variables), la plantilla, a quiénes
 * en palabras (de dónde salen, el filtro por compras, las exclusiones elegidas y las listas que valen siempre), cuántos
 * lo reciben y cuántos quedan afuera (con el link al detalle de los motivos, más abajo), cuándo sale y el costo máximo.
 *
 * - Lo que dice sale de lo guardado y del chequeo (`logic/vasAMandar.ts`): es lo que va a salir, no lo que se tocó.
 * - Envíos grandes (`pideRefuerzo`): al pie, el campo para escribir la cantidad de personas. Sin ventana emergente.
 * - «Cómo se cobra», plegado: el párrafo del cobro vivía en la barra fija y en el celular la hacía crecer.
 * - En el celular, cada renglón pone el título arriba del valor y la previa va debajo.
 */

import { useId, type ReactNode } from "react";
import { ShieldCheck } from "lucide-react";
import type { Chequeo } from "../types";
import type { AudienciaEnPalabras, CuandoDelEnvio } from "../logic/vasAMandar";
import { cuandoSaleEnPalabras, textoCategoria, textoPersonas, textoRefuerzo } from "../logic/vasAMandar";
import { textoPlan } from "../logic/chequeo";
import { usd } from "../logic/formato";
import { num, plural } from "../../campana-nueva/utils/format";

interface Props {
  plantilla: { nombre: string; categoria: string } | null;
  audiencia: AudienciaEnPalabras;
  chequeo: Chequeo;
  /** lo elegido en «Cuándo» (null = todavía nada), la fecha programada (ISO) y la zona de la campaña */
  cuando: CuandoDelEnvio;
  /** la franja en palabras («09:00 a 21:00 (lun a vie)») */
  franjas: string;
  hoy: Date;
  /** la previa real (un contacto de la audiencia); null mientras llega o si no hay ejemplo */
  previa: ReactNode;
  /** lo que se muestra en lugar de la previa cuando no hay */
  sinPrevia: ReactNode;
  /** lleva al detalle de los motivos (el resumen del chequeo, más abajo) */
  onVerDetalle: () => void;
  /** la confirmación reforzada: sólo si `pide` */
  refuerzo: { pide: boolean; escrito: string; onEscrito: (s: string) => void };
}

function Renglon({ titulo, children, testid }: { titulo: string; children: ReactNode; testid?: string }) {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[7.5rem_minmax(0,1fr)] sm:gap-3" data-testid={testid}>
      <dt className="text-[11px] font-bold uppercase tracking-wider text-slate-500 sm:pt-0.5">{titulo}</dt>
      <dd className="min-w-0 text-sm text-slate-800">{children}</dd>
    </div>
  );
}

export function VasAMandar({ plantilla, audiencia, chequeo, cuando, franjas, hoy, previa, sinPrevia, onVerDetalle, refuerzo }: Props) {
  const idTitulo = useId();
  const idRefuerzo = useId();
  const afuera = Math.max(0, (Number(chequeo.total) || 0) - (Number(chequeo.incluidos) || 0));
  // el mismo texto que «Tu campaña» (`cuandoSaleEnPalabras`); lo que falta elegir, acá como un pedido
  const cuandoSale = cuandoSaleEnPalabras(cuando, chequeo)
    ?? <span className="font-semibold text-amber-800">{cuando.modo === null ? "Falta elegir: «Ahora» o «Programar»." : "Elegí la fecha y la hora en «Cuándo»."}</span>;

  return (
    <section aria-labelledby={idTitulo} data-testid="vas-a-mandar" className="space-y-4 rounded-2xl border border-violet-200 bg-white p-4 shadow-sm sm:p-5">
      <h4 id={idTitulo} className="text-base font-semibold tracking-tight text-slate-900">Vas a mandar</h4>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,300px)]">
        <dl className="space-y-3">
          <Renglon titulo="Plantilla" testid="vam-plantilla">
            {plantilla ? <><span className="font-semibold">«{plantilla.nombre}»</span> <span className="text-slate-500">· {textoCategoria(plantilla.categoria)}</span></> : "—"}
          </Renglon>
          <Renglon titulo="A quiénes" testid="vam-audiencia">
            <p className="font-medium">{audiencia.origen}</p>
            {audiencia.compras.length > 0 && <p className="text-slate-600">Por compras: {audiencia.compras.join(" · ")}</p>}
            {audiencia.elegidas.map((t) => <p key={t} className="text-slate-600">{t}</p>)}
            {audiencia.siempre && <p className="text-slate-600">{audiencia.siempre}</p>}
          </Renglon>
          <Renglon titulo="Cuántos" testid="vam-cuantos">
            <p>
              <strong className="text-violet-700">{textoPersonas(chequeo.envio)}</strong> {plural(chequeo.envio, "lo recibe", "lo reciben")}
              {" · "}{num(afuera)} {plural(afuera, "queda", "quedan")} afuera
              {afuera > 0 && (
                <> · <button type="button" onClick={onVerDetalle} className="font-semibold text-violet-700 underline">Ver por qué</button></>
              )}
            </p>
          </Renglon>
          <Renglon titulo="Cuándo" testid="vam-cuando">
            <p>{cuandoSale}</p>
            <p className="text-slate-600">Dentro de la franja {franjas || "de la marca"}. {textoPlan(chequeo.plan, hoy, cuando.zona)}</p>
          </Renglon>
          <Renglon titulo="Costo máximo" testid="vam-costo">
            <p><strong>{usd(chequeo.costo.clienteMaximoUSD)}</strong> <span className="text-slate-500">del saldo, como mucho</span></p>
            <details className="mt-1 text-xs text-slate-600" data-testid="como-se-cobra">
              <summary className="cursor-pointer font-semibold text-violet-700">Cómo se cobra</summary>
              {/* Sin «Twilio»: el dueño no sabe qué es. Lo que Twilio cobra es, para él, «el cargo del envío». */}
              <p className="mt-1">
                Se descuentan hasta {usd(chequeo.costo.clienteMaximoUSD)} del saldo a medida que salen. Lo que Meta no entrega se reintegra, salvo el cargo del
                envío (USD 0,001 por fallido y USD 0,005 por no entregado, más el recargo); lo que no se llega a confirmar queda cobrado hasta que se revise.
              </p>
            </details>
          </Renglon>
        </dl>
        <div className="min-w-0 space-y-1" data-testid="vam-previa">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">El mensaje</p>
          {previa ?? sinPrevia}
        </div>
      </div>
      {refuerzo.pide && (
        <div className="rounded-xl bg-amber-50 px-3 py-3 text-sm text-amber-900" data-testid="refuerzo">
          <label htmlFor={idRefuerzo} className="flex items-start gap-2 font-semibold">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" />
            <span>Es un envío grande. {textoRefuerzo(chequeo.envio)}:</span>
          </label>
          <input id={idRefuerzo} inputMode="numeric" autoComplete="off" value={refuerzo.escrito} onChange={(e) => refuerzo.onEscrito(e.target.value)}
            placeholder={num(chequeo.envio)}
            className="mt-2 w-full max-w-[12rem] rounded-lg border border-amber-300 bg-white px-3 py-2 text-base tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
      )}
    </section>
  );
}

export default VasAMandar;
